// Input validation schemas using Zod for type safety and security
import { z } from "zod";

// Common validation patterns
const uuidSchema = z.string().uuid();
const nonEmptyString = z.string().min(1, "This field is required");
const optionalString = z.string().optional();
const urlSchema = z.string().url().optional();

// Post validation schemas
export const createPostSchema = z.object({
  caption: z.string().max(5000, "Caption must be less than 5000 characters").optional(),
  image_url: urlSchema,
  alt_text: z.string().max(255, "Alt text must be less than 255 characters").optional(),
  is_tip: z.boolean().default(false),
  group_id: uuidSchema.optional(),
  category_id: uuidSchema.optional(),
});

export const updatePostSchema = createPostSchema.partial();

// Comment validation schemas
export const createCommentSchema = z.object({
  content: nonEmptyString.max(1000, "Comment must be less than 1000 characters"),
  post_id: uuidSchema,
  parent_id: uuidSchema.optional(),
  image_url: urlSchema,
  video_url: urlSchema,
});

export const updateCommentSchema = z.object({
  content: nonEmptyString.max(1000, "Comment must be less than 1000 characters"),
});

// Profile validation schemas
export const updateProfileSchema = z.object({
  username: z.string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be less than 30 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores")
    .optional(),
  first_name: z.string().max(50, "First name must be less than 50 characters").optional(),
  last_name: z.string().max(50, "Last name must be less than 50 characters").optional(),
  avatar_url: urlSchema,
  cover_image_url: urlSchema,
  social_links: z.object({
    instagram: optionalString,
    twitter: optionalString,
    facebook: optionalString,
    youtube: optionalString,
    website: optionalString,
    linkedin: optionalString,
    github: optionalString,
    patreon: optionalString,
    "500px": optionalString,
  }).optional(),
});

// Review validation schemas
export const createReviewSchema = z.object({
  entity_id: uuidSchema,
  title: nonEmptyString.max(100, "Title must be less than 100 characters"),
  content: nonEmptyString.max(2000, "Review must be less than 2000 characters"),
  rating: z.number().int().min(1, "Rating must be at least 1").max(5, "Rating must be at most 5"),
});

export const updateReviewSchema = createReviewSchema.partial().omit({ entity_id: true });

// Group validation schemas
export const createGroupSchema = z.object({
  name: nonEmptyString.max(100, "Group name must be less than 100 characters"),
  description: z.string().max(500, "Description must be less than 500 characters").optional(),
  cover_image_url: urlSchema,
});

export const updateGroupSchema = createGroupSchema.partial();

// Checklist validation schemas
export const createChecklistTemplateSchema = z.object({
  title: nonEmptyString.max(100, "Title must be less than 100 characters"),
  description: z.string().max(500, "Description must be less than 500 characters").optional(),
  is_public: z.boolean().default(false),
});

export const createChecklistItemSchema = z.object({
  template_id: uuidSchema,
  title: nonEmptyString.max(200, "Title must be less than 200 characters"),
  description: z.string().max(500, "Description must be less than 500 characters").optional(),
  order_index: z.number().int().min(0),
  type: z.enum(["item", "section"]).default("item"),
  links: z.array(z.object({
    title: nonEmptyString,
    url: z.string().url(),
  })).default([]),
});

// Message validation schemas
export const createMessageSchema = z.object({
  recipient_id: uuidSchema,
  content: nonEmptyString.max(1000, "Message must be less than 1000 characters"),
});

// Search validation schemas
export const searchSchema = z.object({
  query: nonEmptyString.max(100, "Search query must be less than 100 characters"),
  type: z.enum(["all", "posts", "people", "groups", "tips"]).default("all"),
  limit: z.number().int().min(1).max(50).default(20),
});

// Contest validation schemas
export const createContestSubmissionSchema = z.object({
  contest_id: uuidSchema,
  image_url: z.string().url("Valid image URL is required"),
  caption: z.string().max(500, "Caption must be less than 500 characters").optional(),
});

// Disney entity validation schemas
export const createDisneyEntitySchema = z.object({
  name: nonEmptyString.max(100, "Name must be less than 100 characters"),
  category: nonEmptyString.max(50, "Category must be less than 50 characters"),
  location: nonEmptyString.max(100, "Location must be less than 100 characters"),
  description: z.string().max(1000, "Description must be less than 1000 characters").optional(),
});

// Support request validation schemas
export const createSupportRequestSchema = z.object({
  name: nonEmptyString.max(100, "Name must be less than 100 characters"),
  email: z.string().email("Valid email address is required"),
  message: nonEmptyString.max(2000, "Message must be less than 2000 characters"),
});

// Auth validation schemas
export const signUpSchema = z.object({
  firstName: nonEmptyString.max(50, "First name must be less than 50 characters"),
  lastName: nonEmptyString.max(50, "Last name must be less than 50 characters"),
  username: z.string()
    .min(3, "Username must be at least 3 characters")
    .max(30, "Username must be less than 30 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
  email: z.string().email("Valid email address is required"),
  password: z.string().min(8, "Password must be at least 8 characters").max(100),
  confirmPassword: z.string(),
}).refine((d) => d.password === d.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export type SignUpInput = z.infer<typeof signUpSchema>;

// Admin validation schemas
export const bulkDeleteSchema = z.object({
  post_ids: z.array(uuidSchema).min(1, "At least one post must be selected"),
});

// Type exports for use in components
export type CreatePostInput = z.infer<typeof createPostSchema>;
export type UpdatePostInput = z.infer<typeof updatePostSchema>;
export type CreateCommentInput = z.infer<typeof createCommentSchema>;
export type UpdateCommentInput = z.infer<typeof updateCommentSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type CreateReviewInput = z.infer<typeof createReviewSchema>;
export type UpdateReviewInput = z.infer<typeof updateReviewSchema>;
export type CreateGroupInput = z.infer<typeof createGroupSchema>;
export type UpdateGroupInput = z.infer<typeof updateGroupSchema>;
export type CreateChecklistTemplateInput = z.infer<typeof createChecklistTemplateSchema>;
export type CreateChecklistItemInput = z.infer<typeof createChecklistItemSchema>;
export type CreateMessageInput = z.infer<typeof createMessageSchema>;
export type SearchInput = z.infer<typeof searchSchema>;
export type CreateContestSubmissionInput = z.infer<typeof createContestSubmissionSchema>;
export type CreateDisneyEntityInput = z.infer<typeof createDisneyEntitySchema>;
export type CreateSupportRequestInput = z.infer<typeof createSupportRequestSchema>;
export type BulkDeleteInput = z.infer<typeof bulkDeleteSchema>;